import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, MapPin, Calendar, Flag, Clock, Package, Navigation, CheckCircle2, SearchX, Loader2, Satellite, ArrowLeft, User, UserCheck, Weight, Boxes } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtTime, copyText, trackingLink } from "@/lib/format";
import { TrackingForm } from "@/components/site/TrackingForm";
import { StatusBadge } from "@/components/tracking/StatusBadge";
import { ProgressTracker } from "@/components/tracking/ProgressTracker";
import { Timeline } from "@/components/tracking/Timeline";

const POLL_MS = 8000;

const Info = ({ icon: Icon, label, value, testId }) => (
  <div className="flex gap-3 py-3 border-b border-slate-100 last:border-0">
    <Icon className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-semibold text-slate-900 break-words" data-testid={testId}>{value || "—"}</p>
    </div>
  </div>
);

const NotFoundBox = ({ message, code, title = "Spedizione non trovata" }) => (
  <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="tracking-not-found">
    <SearchX className="h-14 w-14 text-[#E50914] mx-auto" />
    <h1 className="mt-6 font-display text-3xl sm:text-4xl font-black uppercase text-white" data-testid="tracking-error-title">{title}</h1>
    <p className="mt-3 text-slate-400" data-testid="tracking-error-message">{message}</p>
    <p className="mt-1 font-mono text-slate-500">{code}</p>
    <div className="mt-10 text-left"><TrackingForm size="compact" testPrefix="notfound" /></div>
  </div>
);

const GpsCard = ({ gps }) => (
  <div className="bg-white border border-slate-200 p-6" data-testid="gps-card">
    <div className="flex items-center gap-2">
      <Satellite className="h-5 w-5 text-slate-500" />
      <h3 className="font-display text-lg font-bold uppercase">Tracking GPS</h3>
    </div>
    {gps?.available ? (
      <div className="mt-3 text-sm text-slate-700">
        <p>Ultima posizione rilevata: <span className="font-mono">{gps.position.lat.toFixed(4)}, {gps.position.lng.toFixed(4)}</span></p>
        <p className="text-xs text-slate-500 mt-1">Rilevata il {fmtDateTime(gps.position.recorded_at)} · Fonte: {gps.position.source}</p>
        <a className="mt-3 inline-block text-[#E50914] font-semibold" target="_blank" rel="noopener noreferrer" href={`https://www.openstreetmap.org/?mlat=${gps.position.lat}&mlon=${gps.position.lng}#map=13/${gps.position.lat}/${gps.position.lng}`}>Apri sulla mappa</a>
      </div>
    ) : (
      <p className="mt-3 text-sm text-slate-600" data-testid="gps-unavailable">
        Posizione GPS del mezzo non disponibile. Gli aggiornamenti mostrati in questa pagina sono gli eventi registrati dal nostro personale; la posizione in tempo reale sarà disponibile solo dopo l'integrazione con una fonte GPS autorizzata.
      </p>
    )}
  </div>
);

function useTracking(code) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const prev = useRef(null);
  useEffect(() => {
    let alive = true;
    let stopped = false;
    prev.current = null;
    setData(null);
    setError(null);
    const load = async () => {
      if (stopped || document.hidden) return;
      try {
        const res = await api.get(`/track/${encodeURIComponent(code)}`);
        if (!alive) return;
        if (prev.current && prev.current !== res.data.last_update) toast.success("Nuovo aggiornamento della spedizione");
        prev.current = res.data.last_update;
        setData(res.data);
        setError(null);
      } catch (e) {
        if (!alive) return;
        const st = e.response?.status;
        if (st === 404 || st === 400) stopped = true;
        setError({ status: st, message: formatApiError(e) });
      } finally {
        if (alive) setLastCheck(new Date().toISOString());
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [code]);
  return { data, error, lastCheck };
}

export default function TrackResult() {
  const code = (useParams().code || "").toUpperCase();
  const { data, error, lastCheck } = useTracking(code);

  if (!data && error && [400, 404].includes(error.status)) return <NotFoundBox message={error.message} code={code} />;
  if (!data && error) return <NotFoundBox message={error.message} code={code} title={error.status === 429 ? "Troppe richieste" : "Servizio momentaneamente non disponibile"} />;
  if (!data) {
    return <div className="py-40 flex justify-center" data-testid="tracking-loading"><Loader2 className="h-8 w-8 animate-spin text-[#E50914]" /></div>;
  }
  const delivered = data.status === "CONSEGNATA";
  return (
    <div className="bg-slate-50 min-h-screen" data-testid="tracking-result">
      <section className="bg-[#0B0C0E] relative grain speed-lines border-b border-white/10">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <Link to="/traccia" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-400 hover:text-white" data-testid="tracking-back-link"><ArrowLeft className="h-3.5 w-3.5" /> Nuova ricerca</Link>
          <div className="mt-6 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E50914]">Spedizione</p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <h1 className="font-mono font-bold tracking-wider text-3xl sm:text-4xl text-white" data-testid="tracking-code">{data.code}</h1>
                <button onClick={() => copyText(trackingLink(data.code), "Link di tracciamento copiato")} className="p-2 text-slate-400 hover:text-white border border-white/15 hover:border-white/40 transition-colors" aria-label="Copia link" data-testid="tracking-copy-link-btn"><Copy className="h-4 w-4" /></button>
              </div>
              <div className="mt-4"><StatusBadge status={data.status} size="lg" testId="tracking-status-badge" /></div>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="tracking-live-indicator">
              <span className="live-dot h-2.5 w-2.5 rounded-full bg-emerald-500" />
              Aggiornamento automatico attivo · ultimo controllo {fmtTime(lastCheck)}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {delivered && (
          <div className="flex items-start gap-4 bg-emerald-50 border border-emerald-200 p-6" data-testid="delivery-confirmation">
            <CheckCircle2 className="h-8 w-8 text-emerald-600 shrink-0" />
            <div>
              <h2 className="font-display text-2xl font-bold uppercase text-emerald-800">Spedizione consegnata</h2>
              <p className="text-emerald-700">Consegna confermata il {fmtDateTime(data.delivered_at)}.</p>
            </div>
          </div>
        )}
        <div className="bg-white border border-slate-200 p-6 sm:p-8 pb-8">
          <ProgressTracker status={data.status} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 bg-white border border-slate-200 p-6 sm:p-8">
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-900 mb-8">Cronologia eventi</h2>
            <Timeline events={data.events} />
          </div>
          <aside className="lg:col-span-4 space-y-6">
            <div className="bg-white border border-slate-200 p-6" data-testid="tracking-details">
              <h3 className="font-display text-lg font-bold uppercase mb-2">Dettagli</h3>
              <Info icon={Navigation} label="Località attuale" value={data.current_location} testId="detail-current-location" />
              <Info icon={User} label="Mittente" value={data.sender_name} testId="detail-sender" />
              <Info icon={MapPin} label="Partenza" value={[data.origin_address, data.origin_city].filter(Boolean).join(", ")} testId="detail-origin" />
              <Info icon={UserCheck} label="Destinatario" value={data.recipient_name} testId="detail-recipient" />
              <Info icon={Flag} label="Destinazione" value={[data.destination_address, data.destination_city].filter(Boolean).join(", ")} testId="detail-destination" />
              <Info icon={Calendar} label="Data di affidamento" value={fmtDate(data.pickup_date)} testId="detail-pickup-date" />
              <Info icon={Calendar} label="Consegna prevista" value={fmtDate(data.expected_delivery)} testId="detail-expected-delivery" />
              <Info icon={Clock} label="Ultimo aggiornamento" value={fmtDateTime(data.last_update)} testId="detail-last-update" />
              <Info icon={Package} label="Colli" value={data.packages} testId="detail-packages" />
              <Info icon={Weight} label="Peso" value={data.weight_kg ? `${data.weight_kg} kg` : null} testId="detail-weight" />
              <Info icon={Boxes} label="Tipologia merce" value={data.goods_type} testId="detail-goods-type" />
            </div>
            <GpsCard gps={data.gps} />
            <div className="bg-[#0B0C0E] p-6">
              <p className="text-white font-display font-bold uppercase mb-3">Cerca un'altra spedizione</p>
              <TrackingForm size="compact" testPrefix="result" />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
